import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import UpdateNotificationService from "@common/services/UpdateNotificationService";
import { ArrowUpCircle } from "lucide-react";
import { useEffect, useState } from "react";
import UpdateNotificationModal from "./update-notification-modal";
import UpdateTestButton from "./update-test-button";

/**
 * Returns true while the Display Settings spotlight preview is active
 * (the side-panel toggles `html.bt-preview-active` on the documentElement
 * for the duration of the hover). We subscribe via MutationObserver so
 * the component re-renders the placeholder in and out as the user hovers
 * different rows. Reading `documentElement` works from inside a shadow
 * root too — the class lives on the light-DOM root and we just query
 * the global document.
 */
const useIsPreviewActive = (): boolean => {
  const [active, setActive] = useState<boolean>(() => {
    if (typeof document === "undefined") return false;
    return document.documentElement.classList.contains("bt-preview-active");
  });
  useEffect(() => {
    if (typeof document === "undefined") return;
    const check = () =>
      setActive(
        document.documentElement.classList.contains("bt-preview-active"),
      );
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return active;
};

/**
 * Update Notification Badge Component
 * 
 * Displays a pulsing badge in the top header when an extension update is available.
 * Clicking the badge opens the update modal.
 * Also includes a development test button.
 */
export default function UpdateNotificationBadge() {
  const dispatch = useAppDispatch();
  const [modalOpen, setModalOpen] = useState(false);

  const updateAvailable = useAppSelector(
    (state) => state.UpdateNotificationReducer?.updateAvailable
  );
  const newVersion = useAppSelector(
    (state) => state.UpdateNotificationReducer?.newVersion
  );
  // When Display Settings is showing a hover preview AND no real update
  // is available, render a faded "Update Available" placeholder so the
  // spotlight has a visible target. Without this the wrapper renders
  // empty (0×0) and the cutout silently drops the rep.
  const isPreviewActive = useIsPreviewActive();
  const showPreviewPlaceholder = !updateAvailable && isPreviewActive;

  // Initialize the update notification service (singleton - safe to call multiple times)
  useEffect(() => {
    UpdateNotificationService.initialize();
  }, []);

  const handleBadgeClick = () => {
    setModalOpen(true);
  };


  return (
    <>
      {/* Test Button - Development Only */}
      {/* <UpdateTestButton /> */}

      {/* Update Badge - Only shows when update is available */}
      {updateAvailable && (
        <Button
          onClick={handleBadgeClick}
          variant="ghost"
          size="sm"
          className="relative gap-2 px-3 py-1 h-auto text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-950/30 transition-colors"
        >
          {/* Pulsing indicator */}
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
          </span>

          <ArrowUpCircle className="h-4 w-4" />

          <span>Update Available</span>

          {newVersion && (
            <Badge
              variant="secondary"
              className="ml-1 text-[10px] px-1.5 py-0 h-4 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
            >
              v{newVersion}
            </Badge>
          )}
        </Button>
      )}

      {/* Preview-only placeholder. Mirrors the real badge styling at a
          lower opacity so the spotlight cutout has a meaningful target
          while the user hovers the `updateBadge` row in Display
          Settings. Inert (`pointer-events-none`, no onClick) so it
          can't be tapped accidentally if a preview gets stuck. */}
      {showPreviewPlaceholder && (
        <span
          aria-hidden
          className="relative inline-flex items-center gap-2 px-3 py-1 h-auto text-xs font-medium text-blue-600 dark:text-blue-400 opacity-60 pointer-events-none rounded-md"
        >
          <ArrowUpCircle className="h-4 w-4" />
          <span>Update Available</span>
        </span>
      )}

      <UpdateNotificationModal
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </>
  );
}
